import { ExecutiveSummaryUpgrades } from "./ExecutiveSummaryExtras";
import { useState, type ReactNode } from "react";
import { t } from "./i18n";
import styled, { keyframes } from "styled-components";
import { FiCheck, FiAlertTriangle, FiFlag, FiInfo, FiTrendingUp, FiMapPin, FiHome } from "react-icons/fi";
import { ExecutiveSummaryPanels, type OperationPanelsData } from "./ExecutiveSummaryPanels";
import { RisksAndMitigants, type RisksData } from "./RisksAndMitigants";
import {
  chunkProse,
  renderWithFileRefs,
  renderObservations,
  renderSources,
  type FilesMap,
} from "./BlockRenderer";
import {
  FindingsList,
  FindingCardWrapper,
  FindingHeader,
  FindingCategory,
  FindingBadge,
  FindingTitle,
  FindingDescription,
  RibbonRow,
  RibbonLabel,
  ProseParagraph,
} from "./Styles";
import { reportEventBus } from "./ReportEvents";
import { findingCategoryLabel } from "./Labels";
import { WHITE, TINT_GREEN, TINT_AMBER, TINT_RED, BORDER_TINT, WASH, GREEN_ACCENT, BORDER_GREEN, INK, BORDER_SUBTLE, SEMAPHORE, SURFACE_CARD, TEXT_VALUE, TONE } from "../tokens";

// The report only ever reads the tier, so it carries the union rather than
// depending on either app's DTO module.
export type DueDiligenceTier = "basic" | "professional" | "premium";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Finding = any;

// Solid-color circle with a white icon — matches the design's verdict badge
// (e.g. the green checkmark circle), not a light-tint-bg + colored-icon pill.
const SEMAPHORE_COLORS: Record<string, { bg: string; shadow: string; text: string }> = {
  // The sub-line's red is its own, a touch deeper than the semaphore's text red
  // (measured ΔE ~5.3, above the threshold for small text), so it stays here.
  red: { bg: SEMAPHORE.red.fill, shadow: "rgba(192, 57, 43, 0.3)", text: "#b03325" },
  amber: { bg: SEMAPHORE.amber.fill, shadow: "rgba(201, 130, 14, 0.3)", text: SEMAPHORE.amber.text },
  green: { bg: SEMAPHORE.green.fill, shadow: "rgba(53, 164, 110, 0.3)", text: SEMAPHORE.green.text },
};

const OperationRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 1.1rem;
  margin-bottom: 1.25rem;
`;

const OperationCard = styled.div`
  flex: 1 1 420px;
  display: flex;
  flex-direction: column;
  background: ${WHITE};
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 1.25rem;
  padding: 1.5rem 1.6rem;

  @media (max-width: 1100px) {
    flex-basis: 100%;
  }
`;

const OperationOverline = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${GREEN_ACCENT};
`;

const OperationText = styled.p`
  margin: 0.75rem 0 0 0;
  font-size: 1.0625rem;
  font-weight: 500;
  line-height: 1.55;
  color: #3a4a50;

  strong {
    color: ${TEXT_VALUE};
    font-weight: 700;
  }
`;

const OperationVerdictRow = styled.div`
  margin-top: auto;
  padding-top: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.7rem;
`;

const VerdictIconCircle = styled.div<{ $variant: string }>`
  flex-shrink: 0;
  width: 2.375rem;
  height: 2.375rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => SEMAPHORE_COLORS[p.$variant]?.bg ?? SEMAPHORE_COLORS.amber.bg};
  box-shadow: 0 6px 16px ${(p) => SEMAPHORE_COLORS[p.$variant]?.shadow ?? SEMAPHORE_COLORS.amber.shadow};
  color: ${WHITE};
`;

const OperationVerdictLabel = styled.div`
  font-size: 0.95rem;
  font-weight: 800;
  color: ${TEXT_VALUE};
  letter-spacing: -0.01em;
`;

// The sub-line carries the verdict's own colour, the way the design does:
// green when the verdict is green, amber when it is amber.
const OperationVerdictSub = styled.div<{ $variant: string }>`
  margin-top: 0.15rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: ${(p) => SEMAPHORE_COLORS[p.$variant]?.text ?? SEMAPHORE_COLORS.amber.text};
`;

const RecommendationBox = styled.div`
  margin-top: 2.25rem;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  background: #f4f9f6;
  border: 1px solid #e1ede7;
  border-radius: 1rem;
  padding: 1.6rem 1.9rem;
`;

const RecommendationIcon = styled.div`
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: #35a46e;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RecommendationOverline = styled.div`
  font-size: 0.69rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #237a50;
  margin-bottom: 0.5rem;
`;



const OperationScopeNote = styled.div`
  margin-top: 0.85rem;
  padding-top: 0.8rem;
  border-top: 1px solid ${BORDER_SUBTLE};
  font-size: 0.66rem;
  color: ${INK.faint};
  letter-spacing: 0.02em;
`;

const OperationMapBox = styled.div`
  flex: 1 1 280px;
  min-height: 220px;
  border-radius: 1.25rem;
  overflow: hidden;
  border: 1px solid ${BORDER_SUBTLE};
  position: relative;
`;

// Shown inside the map box in place of the map itself. The box always holds
// the cockpit's right-hand column, so what varies is this line, never the
// layout: "on its way" while the cadastral lookup runs, "not available" once
// it has run without yielding a point (or when the tiles fail to load).
const MapNotice = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  text-align: center;
  background: ${WASH};
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #6b7f88;
`;

const MapAddressPill = styled.div`
  position: absolute;
  left: 1rem;
  right: 1rem;
  bottom: 1rem;
  z-index: 400;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${WHITE};
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.75rem;
  padding: 0.56rem 0.8rem;
  box-shadow: 0 2px 10px rgba(20, 50, 59, 0.08);
  font-size: 0.75rem;
  font-weight: 500;
  color: ${TEXT_VALUE};

  svg {
    flex-shrink: 0;
    color: ${SEMAPHORE.green.fill};
  }
`;

// KPI band — 5 fixed operation figures below the summary card. "Valor
// estimado del suelo" (the hero, spanning both rows of column 1) is always
// present per the design: when the case has no computed value yet it falls
// back to "-" / "Calculando" rather than being omitted, since Laura confirmed
// this slot always exists in the layout.
const KpiBand = styled.div`
  display: grid;
  grid-template-columns: 1.12fr 1fr 1fr;
  grid-auto-rows: 1fr;
  gap: 0.75rem;
  margin-top: 1.1rem;
  margin-bottom: 1.25rem;
`;

const KpiHero = styled.div`
  grid-row: span 2;
  display: flex;
  flex-direction: column;
  background: linear-gradient(160deg, ${TINT_GREEN[100]}, ${SURFACE_CARD});
  border: 1px solid ${BORDER_GREEN};
  border-radius: 1rem;
  padding: 1.5rem 1.5rem 1.375rem;
`;

const KpiHeroHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.56rem;
`;

const KpiHeroIconBadge = styled.div`
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.5rem;
  background: ${SEMAPHORE.green.fill};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${WHITE};
`;

const KpiHeroLabel = styled.span`
  font-size: 0.59rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${SEMAPHORE.green.text};
`;

const KpiHeroValue = styled.div`
  margin-top: 0.6rem;
  font-size: 1.9375rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: ${TEXT_VALUE};
`;

const KpiHeroHint = styled.div`
  margin-top: 0.35rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  font-weight: 500;
  line-height: 1.4;
  color: ${INK.soft};
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const CalculatingSpinner = styled.div`
  flex-shrink: 0;
  width: 0.7rem;
  height: 0.7rem;
  border: 2px solid ${BORDER_GREEN};
  border-top-color: ${SEMAPHORE.green.fill};
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

export type KpiTone = typeof TONE.amber | typeof TONE.red;

// A toned card carries its alert in the ground as well as in the figure:
// the design tints the whole card, not just the value.
const KPI_GROUND: Record<KpiTone, { bg: string; border: string }> = {
  amber: { bg: TINT_AMBER, border: BORDER_TINT.amber },
  red: { bg: TINT_RED, border: BORDER_TINT.red },
};
const kpiBg = (tone?: KpiTone) => (tone ? KPI_GROUND[tone].bg : SURFACE_CARD);
const kpiBorder = (tone?: KpiTone) => (tone ? KPI_GROUND[tone].border : BORDER_SUBTLE);
const kpiValueColor = (tone?: KpiTone, accent?: boolean) =>
  tone ? SEMAPHORE[tone].text : accent ? SEMAPHORE.green.text : TEXT_VALUE;

const KpiCard = styled.div<{ $tone?: KpiTone }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: ${(p) => kpiBg(p.$tone)};
  border: 1px solid ${(p) => kpiBorder(p.$tone)};
  border-radius: 0.875rem;
  padding: 1rem 1.125rem;
`;

const KpiCardButton = styled.button<{ $tone?: KpiTone }>`
  all: unset;
  cursor: pointer;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: ${(p) => kpiBg(p.$tone)};
  border: 1px solid ${(p) => kpiBorder(p.$tone)};
  border-radius: 0.875rem;
  padding: 1rem 1.125rem;

  &:hover {
    border-color: #d8e6dd;
  }
`;

const KpiLabel = styled.div`
  font-size: 0.5625rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${INK.label};
`;

const KpiValue = styled.div<{ $accent?: boolean; $tone?: KpiTone }>`
  margin-top: 0.3rem;
  font-size: 1.1875rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${(p) => kpiValueColor(p.$tone, p.$accent)};
`;

const KpiHint = styled.div<{ $accent?: boolean }>`
  margin-top: 0.2rem;
  font-size: 0.72rem;
  font-weight: ${(p) => (p.$accent ? 600 : 400)};
  color: ${(p) => (p.$accent ? GREEN_ACCENT : INK.soft)};
`;

// Same offset-aware smooth scroll as FinalReport/index.tsx's `scrollToBlock`
// (kept separate rather than shared — different module, small enough to not
// warrant new plumbing): without it, a sticky subheader bar covers the
// target's own title, landing the viewport directly on its body content
// instead (caught by Hugo: "se va directísimo al simulador... ha de mostrar
// el título").
function scrollToAnchor(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const subheader = document.querySelector("[data-subheader]") as HTMLElement | null;
  const offset = subheader ? subheader.getBoundingClientRect().bottom : 0;
  const top = el.getBoundingClientRect().top + window.scrollY - offset - 8;
  window.scrollTo({ top, behavior: "smooth" });
}

const VERDICT_ICON: Record<string, JSX.Element> = {
  green: <FiCheck size={17} />,
  amber: <FiAlertTriangle size={17} />,
  red: <FiAlertTriangle size={17} />,
};

const FINDING_ICON = (variant: string) => {
  if (variant === TONE.red) return <FiAlertTriangle size={11} />;
  if (variant === TONE.amber) return <FiFlag size={11} />;
  return <FiInfo size={11} />;
};

export interface OperationKpiItem {
  label: string;
  value: string;
  hint?: string;
  // Renders value/hint in the green "accent" tone (e.g. "Cargas económicas: 0 €").
  accent?: boolean;
  // An alert card ("Situación del titular · Concurso"): tinted ground and
  // value in that tone. The hint keeps its grey.
  tone?: KpiTone;
  onClick?: () => void;
  // Declarative alternative to `onClick` (backend data can't carry a function):
  // the DOM id of an element on this same page to smooth-scroll to when the
  // card is clicked — e.g. "Viviendas potenciales" → the housing simulator's
  // anchor. Generic on purpose, any KPI item can point at any anchored
  // element; not specific to the housing simulator.
  scrollTarget?: string;
}

export interface OperationKpis {
  // Always rendered per the design, even with no computed value yet — falls
  // back to the "-" / "Calculando" placeholder rather than being hidden.
  landValue: { value: string; hint?: string } | null;
  items: OperationKpiItem[];
}

export interface ValuationPreset {
  venta: number;
  coste: number;
  benef: number;
  soft: number;
}

export interface SliderRange {
  min: number;
  max: number;
  step: number;
}

// A slider whose end labels say where each end comes from ("1.770 · P25
// Registro") instead of just repeating the figure.
export type LabelledSliderRange = SliderRange & { minLabel?: string; maxLabel?: string };

// Replacements for a simulator's locale strings, keyed by the leaf key under
// its own namespace (`plotAreaTip`, `rangeNote`…), same `{{value}}` and
// `<1>…</1>` markup as the locale files. The default copy describes the
// property each simulator was first drawn with (Torrent): a report on another
// plot words it for its own planning rules.
export type SimulatorTexts = Partial<Record<string, string>>;

// What the housing simulator needs to know about this property. Everything is
// optional: absent, the simulator behaves as it was first drawn (footprint ×
// residential floors, no density cap, 30 m² usable minimum).
export interface HousingSimulatorConfig {
  plotAreaM2?: number;
  occupancyPct?: number;
  maxFloors?: number;
  // The plot is a given (two registered plots grouped), not an assumption.
  plotAreaFixed?: boolean;
  minFloors?: number;
  initialFloors?: number;
  initialAvgUnitSize?: number;
  initialUsableRatio?: number;
  ranges?: { occupancy?: SliderRange; avgUnitSize?: SliderRange };
  // An envelope ordinance (Casco C-1) caps the built area at `maxRatio` × plot,
  // and stepping on a slope makes the top floor count reach that cap. With it
  // the ground floor is part of the total, and a commercial ground floor is
  // subtracted (up to `groundFloorCommercialM2`) rather than dropping a floor.
  envelope?: { maxRatio: number; fullAtFloors: number; groundFloorCommercialM2: number };
  // Density standard: one dwelling per this many m² of residential floor area.
  densityM2PerUnit?: number;
  // Count the density over the whole capped built area, ground floor included,
  // as a municipal certificate does (Benalmádena: 2 floors × plot ÷ 100).
  densityOverTotal?: boolean;
  minUsableM2?: number;
  // Average sizes behind the "rango orientativo" note.
  unitSizeRange?: { min: number; max: number };
  sensitivitySizes?: number[];
  // Shown instead of the unit count when parking is not required ("0 exigibles");
  // `{{standard}}` stands for the standard's count.
  parkingValue?: string;
  // One more space per this many m² of commercial ground floor, on top of one
  // per dwelling.
  parkingPerCommercialM2?: number;
  // Citation keys behind each superscript, when they are not Torrent's.
  cites?: Partial<Record<"intro" | "plotArea" | "occupancy" | "floors" | "parking" | "usableRatio" | "usableArea" | "disclaimer", string>>;
  texts?: SimulatorTexts;
}

// What the valuation simulator needs to know about this property. The method
// (static residual, ECO/805/2003) is the same for every report; the market it
// runs on is not — a logistics plot in the Corredor del Henares sells at a
// third of a flat in l'Horta Sud. Every field is optional and falls back to
// the residential defaults the simulator was first drawn with, so a report
// that only says `valuationSimulator: true` keeps rendering as before.
export interface ValuationSimulatorConfig {
  plotAreaM2?: number;
  // AJD the buyer pays on top of the land price, in %.
  acquisitionTaxPct?: number;
  // The central preset is also the starting point.
  presets?: { pesimista: ValuationPreset; central: ValuationPreset; optimista: ValuationPreset };
  initialFloorArea?: number;
  initialCharges?: number;
  // The fee on the urbanisation bond, as its own input next to the charges
  // (a cédula that makes you urbanise alongside the building, with a bond).
  // The bond is a guarantee, not an outlay: only its fee comes off the
  // residual. Without it the input is not there.
  guaranteeFee?: { initial?: number };
  // The charges (and the bond's fee) are known to exist but not yet
  // quantified: their captions say so in amber and, while either is zero,
  // a note warns that the result is a ceiling, not a value.
  chargesPending?: boolean;
  ranges?: {
    // `hint` sits between the end labels when there is no housing simulator
    // to resync with, e.g. "techo útil 12.000–19.000 m²t". With a commercial
    // ground floor (`fixedRevenue.groundFloorCommercial`) `max` is the cap
    // with the shop, `maxWithoutCommercial` the one once it becomes housing.
    floorArea?: LabelledSliderRange & { hint?: string; maxWithoutCommercial?: number };
    venta?: LabelledSliderRange;
    coste?: LabelledSliderRange;
    benef?: LabelledSliderRange;
    soft?: LabelledSliderRange;
  };
  // Revenue that does not scale with the housing floor area — the shop,
  // garage and storerooms of a visado project. `groundFloorCommercial` drops
  // out when the synced housing simulator turns the ground floor into housing.
  fixedRevenue?: { amount: number; groundFloorCommercial?: number };
  // A band at two documented sale prices ("2.240 – 3.520 €/m²") instead of
  // the default ±10 % on price, cost and margin.
  priceBand?: { low: number; high: number };
  // Start in manual mode even when a housing simulator could feed the area.
  startUnlinked?: boolean;
  // Units printed after the floor area and the cost ("m²", "€/m²").
  units?: { floorArea?: string; coste?: string };
  // The ⓘ bubbles that quote this property's own sources.
  tips?: { floorArea?: string; venta?: string; coste?: string; cargas?: string; aval?: string; benef?: string; soft?: string; compra?: string };
  disclaimer?: string;
  texts?: SimulatorTexts;
}

interface Props {
  // The map and the two simulators live in the host app, not here: they are
  // 1.700 lines of interactive UI that only the client report mounts, and the
  // housing one feeds the valuation one through this component's state — hence
  // render props rather than plain slots. admin-interface passes none, and the
  // cockpit simply leaves those places empty.
  renderMap?: (args: { lat: number; lng: number; zoom?: number }) => ReactNode;
  renderHousingSimulator?: (args: {
    initialPlotAreaM2?: number;
    initialOccupancyPct?: number;
    maxFloors?: number;
    config?: HousingSimulatorConfig;
    onResidentialFloorAreaChange: (value: number, groundFloorIsHousing?: boolean) => void;
  }) => ReactNode;
  renderValuationSimulator?: (args: {
    linkedFloorArea?: number;
    linkedGroundFloorIsHousing?: boolean;
    plotAreaM2?: number;
    config?: ValuationSimulatorConfig;
  }) => ReactNode;
  // Raw §1.1 block ("Viabilidad de la operación"): essence + findings +
  // observations + sources, exactly as produced by section1Executive.ts.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  block: any;
  coordinates?: { lat: number; lng: number; zoom?: number } | null;
  // True while the cadastral lookup that yields the coordinates is still
  // running: the map box then says the map is on its way instead of saying it
  // is unavailable, which would be wrong on a report seconds old.
  mapPending?: boolean;
  address?: string | null;
  propertyType?: string | null;
  files?: FilesMap;
  sectionsCount?: number;
  tier?: DueDiligenceTier | null;
  kpis?: OperationKpis | null;
  panels?: OperationPanelsData | null;
  housingSimulator?: HousingSimulatorConfig | null;
  valuationSimulator?: boolean | ValuationSimulatorConfig | null;
  risks?: RisksData | null;
  // Starts the checkout for the level the clicked CTA names. Absent when the
  // case can't be upgraded, in which case the upgrade panel falls back to its
  // fixture links.
  onUpsell?: (level: number | null) => void;
  upsellPending?: boolean;
}

// No "Nivel N" convention exists elsewhere in the app (the rest of the
// product uses the business tier names — see dueDiligence.caseDetails.tierLabels),
// but this scope note specifically mirrors the design's "Nivel 3" phrasing.
const TIER_LEVEL: Record<DueDiligenceTier, number> = {
  basic: 1,
  professional: 2,
  premium: 3,
};

// Dedicated "cockpit" view for §1 (Resumen ejecutivo), replacing the generic
// BlockRenderer layout for this one block: the operation summary card (with
// the property map beside it) and the key findings as clickable cards that
// jump to their source section. Built entirely from real report data
// (essence/findings/observations/sources) — no invented KPIs. §1.2
// ("Acciones previas a la firma"), if present, keeps rendering through the
// normal BlockRenderer right below this.
export const ExecutiveSummaryCockpit = ({
  renderMap,
  renderHousingSimulator,
  renderValuationSimulator,
  block,
  coordinates,
  mapPending,
  address,
  propertyType,
  files,
  sectionsCount,
  tier,
  kpis,
  panels,
  housingSimulator,
  valuationSimulator,
  risks,
  onUpsell,
  upsellPending,
}: Props) => {
  // Shared between the two simulators when "sincronizado" — the valuation
  // simulator's edificabilidad vendible tracks the housing simulator's
  // residential floor area unless the user unlinks it.
  const [linkedFloorArea, setLinkedFloorArea] = useState(0);
  // A commercial ground floor turned into housing takes the shop's fixed
  // revenue out of the valuation.
  const [linkedGroundFloorIsHousing, setLinkedGroundFloorIsHousing] = useState(false);
  const essence = block?.essence;
  const findings = (block?.findings || []) as Finding[];
  const variant = essence?.statusBadge?.variant || essence?.semaphore || "amber";
  // A report that names what its verdict covers ("Registral, urbanístico,
  // valoración y contingencias") says it better than the generic risk level.
  const verdictSub = essence?.statusBadge?.sub || [
    t(`dueDiligence.finalReport.cockpit.riskLevel.${variant}`),
    propertyType,
  ]
    .filter(Boolean)
    .join(" · ");

  const hasMap = !!(coordinates?.lat && coordinates?.lng);

  return (
    <>
      {/* The map is property identification, not part of the verdict: its box
          holds this column whether or not the executive summary has been
          written yet, and whether or not there is a point to draw. */}
      <OperationRow>
        {essence && (
          <OperationCard>
            <OperationOverline>
              <FiTrendingUp size={13} /> {t("dueDiligence.finalReport.cockpit.operationSummaryLabel")}
            </OperationOverline>
            {essence.content && <OperationText>{renderWithFileRefs(essence.content, files)}</OperationText>}
            {essence.statusBadge && (
              <OperationVerdictRow>
                <VerdictIconCircle $variant={variant}>
                  {VERDICT_ICON[variant] || VERDICT_ICON.amber}
                </VerdictIconCircle>
                <div>
                  <OperationVerdictLabel>{essence.statusBadge.label}</OperationVerdictLabel>
                  {verdictSub && <OperationVerdictSub $variant={variant}>{verdictSub}</OperationVerdictSub>}
                </div>
              </OperationVerdictRow>
            )}
            {(!!sectionsCount || tier) && (
              <OperationScopeNote>
                {t("dueDiligence.finalReport.cockpit.scopeNote", { count: sectionsCount ?? 0 })}
                {tier && ` · ${t("dueDiligence.finalReport.cockpit.levelLabel", { level: TIER_LEVEL[tier] })}`}
              </OperationScopeNote>
            )}
          </OperationCard>
        )}

        <OperationMapBox>
          {hasMap ? (
            renderMap?.({ lat: coordinates.lat, lng: coordinates.lng, zoom: coordinates.zoom })
          ) : (
            <MapNotice>
              {t(`dueDiligence.finalReport.cockpit.${mapPending ? "mapPending" : "mapUnavailable"}`)}
            </MapNotice>
          )}
          {address && (
            <MapAddressPill>
              <FiMapPin size={13} /> {address}
            </MapAddressPill>
          )}
        </OperationMapBox>
      </OperationRow>

      {block.executiveUpgrades && <ExecutiveSummaryUpgrades data={block.executiveUpgrades} onUpsell={onUpsell} upsellPending={upsellPending} />}

      {kpis && (
        <KpiBand>
          <KpiHero>
            <KpiHeroHeader>
              <KpiHeroIconBadge>
                <FiHome size={15} />
              </KpiHeroIconBadge>
              <KpiHeroLabel>{t("dueDiligence.finalReport.cockpit.kpis.landValueLabel")}</KpiHeroLabel>
            </KpiHeroHeader>
            <KpiHeroValue>
              {kpis.landValue?.value || t("dueDiligence.finalReport.cockpit.kpis.landValuePlaceholder")}
            </KpiHeroValue>
            <KpiHeroHint>
              {kpis.landValue?.hint || (
                <>
                  <CalculatingSpinner />
                  {t("dueDiligence.finalReport.cockpit.kpis.landValueCalculating")}
                </>
              )}
            </KpiHeroHint>
          </KpiHero>

          {kpis.items.map((item, i) => {
            const handleClick =
              item.onClick ||
              (item.scrollTarget ? () => scrollToAnchor(item.scrollTarget!) : undefined);
            return handleClick ? (
              <KpiCardButton key={i} type="button" onClick={handleClick} $tone={item.tone}>
                <KpiLabel>{item.label}</KpiLabel>
                <KpiValue $accent={item.accent} $tone={item.tone}>{item.value}</KpiValue>
                {item.hint && <KpiHint $accent={item.accent}>{item.hint}</KpiHint>}
              </KpiCardButton>
            ) : (
              <KpiCard key={i} $tone={item.tone}>
                <KpiLabel>{item.label}</KpiLabel>
                <KpiValue $accent={item.accent} $tone={item.tone}>{item.value}</KpiValue>
                {item.hint && <KpiHint $accent={item.accent}>{item.hint}</KpiHint>}
              </KpiCard>
            );
          })}
        </KpiBand>
      )}

      {panels && <ExecutiveSummaryPanels panels={panels} files={files} />}

      {housingSimulator && (
        // id targeted by KPI items' `scrollTarget` (e.g. "Viviendas potenciales" →
        // "abrir simulador"), matches the design's own anchor (dc.html "z-vivsim").
        <div id="z-vivsim">
          {renderHousingSimulator?.({
            initialPlotAreaM2: housingSimulator.plotAreaM2,
            initialOccupancyPct: housingSimulator.occupancyPct,
            maxFloors: housingSimulator.maxFloors,
            config: housingSimulator,
            onResidentialFloorAreaChange: (value, groundFloorIsHousing) => {
              setLinkedFloorArea(value);
              setLinkedGroundFloorIsHousing(!!groundFloorIsHousing);
            },
          })}
        </div>
      )}

      {/* Both simulators are gated by independent flags, but only the housing
          one feeds `linkedFloorArea`. Without it the link has nothing behind
          it, so pass `undefined` and let the valuation simulator start manual. */}
      {valuationSimulator && renderValuationSimulator?.({
        linkedFloorArea: housingSimulator ? linkedFloorArea : undefined,
        linkedGroundFloorIsHousing: housingSimulator ? linkedGroundFloorIsHousing : undefined,
        plotAreaM2: (typeof valuationSimulator === "object" ? valuationSimulator.plotAreaM2 : undefined) ?? housingSimulator?.plotAreaM2,
        config: typeof valuationSimulator === "object" ? valuationSimulator : undefined,
      })}

      {risks && <RisksAndMitigants data={risks} sectionsCount={sectionsCount} tier={tier} />}

      {findings.length > 0 && (
        <>
          <RibbonRow>
            <RibbonLabel>{t("dueDiligence.finalReport.cockpit.riskAreasLabel")}</RibbonLabel>
          </RibbonRow>
          <FindingsList>
            {findings.map((f, i) => {
              const clickable = !!f.sourceSection;
              return (
                <FindingCardWrapper
                  key={i}
                  $category={f.category}
                  onClick={clickable ? () => reportEventBus.emit("REPORT_SCROLL_TO_SECTION", `section-${f.sourceSection}`) : undefined}
                  style={clickable ? { cursor: "pointer" } : undefined}
                >
                  <FindingHeader>
                    <FindingCategory $category={f.category || "informative"}>
                      {findingCategoryLabel(f.category)}
                    </FindingCategory>
                    {f.badge && (
                      <FindingBadge $variant={f.badge.variant || "info"}>
                        {FINDING_ICON(f.badge.variant)}
                        {f.badge.label}
                      </FindingBadge>
                    )}
                  </FindingHeader>
                  {f.title && <FindingTitle>{renderWithFileRefs(f.title, files)}</FindingTitle>}
                  {f.description && <FindingDescription>{renderWithFileRefs(f.description, files)}</FindingDescription>}
                </FindingCardWrapper>
              );
            })}
          </FindingsList>
        </>
      )}

      {/* The strategic recommendation closes the executive summary, as its own
          green panel — not as a second paragraph of the verdict card. */}
      {essence?.extra && (
        <RecommendationBox>
          <RecommendationIcon>
            <FiCheck size={14} strokeWidth={3} />
          </RecommendationIcon>
          <div>
            <RecommendationOverline>
              {t("dueDiligence.finalReport.cockpit.recommendation.overline")}
            </RecommendationOverline>
            {/* Same paragraphing as the rest of the report's prose: chunked by
                sentence, first chunk as the lead. */}
            {chunkProse(essence.extra).map((chunk, i) => (
              <ProseParagraph key={i} $lead={i === 0}>
                {renderWithFileRefs(chunk, files)}
              </ProseParagraph>
            ))}
          </div>
        </RecommendationBox>
      )}

      {renderObservations(block?.observations, files)}
      {renderSources(block?.sources, files)}
    </>
  );
};
